/* eslint-disable @typescript-eslint/no-explicit-any */
import { AxiosError } from 'axios'
import { useAuthStore } from '@/stores/authStore'
import { axiosInstance } from '@/lib/axios'

const API_URL = import.meta.env.VITE_API_URL

class BaseService {
  protected getAccessToken() {
    return useAuthStore.getState().auth.accessToken
  }

  protected async get<T>(
    url: string,
    params?: Record<string, any>
  ): Promise<T> {
    try {
      const response = await axiosInstance.get<T>(`${API_URL}${url}`, {
        params,
        headers: {
          Authorization: `Bearer ${this.getAccessToken()}`,
        },
      })
      return response.data
    } catch (error) {
      if (error instanceof AxiosError && error.response?.data) {
        throw error.response.data
      }
      throw error
    }
  }

  protected async post<T>(
    url: string,
    data?: unknown,
    isFormData = false,
    params?: any
  ): Promise<T> {
    try {
      const headers: Record<string, string> = {
        Authorization: `Bearer ${this.getAccessToken()}`,
      }
      if (isFormData) {
        headers['Content-Type'] = 'multipart/form-data'
      }
      const response = await axiosInstance.post<T>(url, data, {
        headers,
        params,
      })
      return response.data
    } catch (error) {
      if (error instanceof AxiosError && error.response?.data) {
        throw error.response.data
      }
      throw error
    }
  }

  protected async put<T>(
    url: string,
    data?: any,
    isFormData = false
  ): Promise<T> {
    try {
      const headers: Record<string, string> = {
        Authorization: `Bearer ${this.getAccessToken()}`,
      }
      if (isFormData) {
        headers['Content-Type'] = 'multipart/form-data'
      }
      const response = await axiosInstance.put<T>(`${API_URL}${url}`, data, {
        headers,
      })
      return response.data
    } catch (error) {
      if (error instanceof AxiosError && error.response?.data) {
        throw error.response.data
      }
      throw error
    }
  }

  protected async delete<T>(url: string, data?: any): Promise<T> {
    try {
      const response = await axiosInstance.delete<T>(`${API_URL}${url}`, {
        data,
        headers: {
          Authorization: `Bearer ${this.getAccessToken()}`,
        },
      })
      return response.data
    } catch (error) {
      if (error instanceof AxiosError && error.response?.data) {
        throw error.response.data
      }
      throw error
    }
  }

  protected async deleteMultiple<T>(url: string, ids: string[]): Promise<T> {
    try {
      const response = await axiosInstance.post<T>(
        `${API_URL}${url}`,
        { ids },
        {
          headers: {
            Authorization: `Bearer ${this.getAccessToken()}`,
          },
        }
      )
      return response.data
    } catch (error) {
      if (error instanceof AxiosError && error.response?.data) {
        throw error.response.data
      }
      throw error
    }
  }

  protected async patch<T>(url: string, data?: any): Promise<T> {
    try {
      const response = await axiosInstance.patch<T>(`${API_URL}${url}`, data, {
        headers: {
          Authorization: `Bearer ${this.getAccessToken()}`,
        },
      })
      return response.data
    } catch (error) {
      if (error instanceof AxiosError && error.response?.data) {
        throw error.response.data
      }
      throw error
    }
  }

  protected async uploadFile<T>(url: string, file: File): Promise<T> {
    const formData = new FormData()
    formData.append('file', file)

    return this.post<T>(url, formData, true)
  }
}

export default BaseService
